import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, ShieldCheck, ArrowLeft } from 'lucide-react';
import { adminApi, setAdminToken } from '../../services/adminApi';

/**
 * Super-admin login: two-step email OTP flow.
 *   Step 1: enter email → POST /admin/otp/request (always succeeds, opaque)
 *   Step 2: enter 6-digit code → POST /admin/otp/verify → JWT → /admin
 */
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await adminApi.requestOtp(email.trim());
      toast.success('If that email is registered, a 6-digit code is on its way.');
      setStep('code');
    } catch (err) {
      const msg = err.response?.data?.detail;
      // Surface rate-limit errors explicitly
      if (err.response?.status === 429) {
        toast.error('Too many requests — please wait 15 minutes.');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Could not send code. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      toast.error('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi.verifyOtp(email.trim(), trimmed);
      setAdminToken(data.token);
      toast.success(`Welcome, ${data.admin.name || data.admin.email}`);
      navigate('/admin', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-background min-h-screen flex items-center justify-center p-6"
      data-testid="admin-login-page"
    >

      <div className="w-full max-w-md relative z-10">
        <div className="bg-[#1a1a1a]/95 backdrop-blur rounded-2xl border border-white/5 shadow-2xl p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#FFD108] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-black" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFD108] font-semibold">
                Super Admin
              </p>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                EQUIPR Guardian
              </h1>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-8">
            Sign in with a one-time code sent to your email.
          </p>

          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-5" data-testid="admin-email-form">
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@equipr.software"
                    className="w-full h-12 pl-10 pr-3 rounded-lg bg-[#262626] border border-white/5 text-white placeholder:text-muted-foreground focus:outline-none focus:border-[#FFD108]"
                    required
                    autoFocus
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full h-12 rounded-lg bg-[#FFD108] text-black font-semibold uppercase tracking-wide hover:bg-[#E5B93D] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="admin-send-code-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Sending…' : 'Send code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className="space-y-5" data-testid="admin-code-form">
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full h-14 px-4 rounded-lg bg-[#262626] border border-white/5 text-white text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:border-[#FFD108]"
                  required
                  autoFocus
                  data-testid="admin-code-input"
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Sent to <span className="text-white">{email}</span> · expires in 10 minutes
                </p>
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-12 rounded-lg bg-[#FFD108] text-black font-semibold uppercase tracking-wide hover:bg-[#E5B93D] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="admin-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? 'Verifying…' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); }}
                className="w-full text-xs text-muted-foreground hover:text-white flex items-center justify-center gap-1 transition-colors"
                data-testid="admin-back-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Use a different email
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Not an admin?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-[#FFD108] hover:underline"
            data-testid="admin-back-to-login"
          >
            Go to the tenant login →
          </button>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
